import React from 'react';
import type {
  CoreModelPriceTable,
  CoreModelPriceTableItem,
  CoreModelPriceTableMemberPrice,
} from '$/api/server.types';
import { useLanguageStore, type LanguageStoreType } from '$/store/languageStore';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Table';
import IconFont from '../../IconFont';
import styles from './index.module.scss';

export interface CoreModalPricingProps {
  priceTable?: CoreModelPriceTable;
}

type TranslateText = LanguageStoreType['languageStore']['smartcatTranslationText'];

const MODEL_TYPE_KEY_MAP: Record<string, Parameters<TranslateText>[0]> = {
  video: (v) => v.roboneo_web_video,
  image: (v) => v.roboneo_web_image,
};

const CONSUME_UNIT_KEY_MAP: Record<string, Parameters<TranslateText>[0] | string> = {
  second: (v) => v.seconds_left,
  image: (v) => v['78'],
};

interface MemberColumn {
  key: string;
  title: string;
  level: number;
}

// getMemberColumns 使用接口表头生成会员列，列顺序以后端返回为准。
function getMemberColumns(priceTable?: CoreModelPriceTable) {
  return (priceTable?.headers || [])
    .filter((header) => header.key !== 'model')
    .map((header) => ({
      key: header.key,
      title: header.label,
      level: Number(header.key.match(/level_(\d+)/)?.[1]) || 0,
    }))
    .filter((column) => column.level > 0);
}

// formatNumber 保留后端下发的小数精度，同时去掉无意义的末尾 0。
function formatNumber(value?: number | string) {
  if (typeof value === 'string') return value;
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return `${Number(value.toFixed(4))}`;
}

// getModelTypeText 把后端枚举和展示文案转成 Figma 行副标题文案。
function getModelTypeText(item: CoreModelPriceTableItem, translate: TranslateText) {
  if (!item.modelType && !item.modelTypeLabel) return '-';
  const modelTypeKey = item.modelType ? MODEL_TYPE_KEY_MAP[item.modelType] : undefined;
  const text = item.modelTypeLabel || (modelTypeKey ? translate(modelTypeKey) : item.modelType);
  const modelText = translate((v) => v.roboneo_model);
  const separator = /^[A-Za-z]/.test(modelText) ? ' ' : '';
  return `${text}${separator}${modelText}`;
}

// getConsumeUnitText 把后端消耗单位枚举转成用户可读单位。
function getConsumeUnitText(
  consumeUnit: string | undefined,
  consumeUnitLabel: string | undefined,
  translate: TranslateText,
) {
  if (consumeUnitLabel) return consumeUnitLabel;
  if (!consumeUnit) return '-';
  const consumeUnitKey = CONSUME_UNIT_KEY_MAP[consumeUnit];
  const consumeUnitText = typeof consumeUnitKey === 'function' ? translate(consumeUnitKey) : consumeUnitKey;
  return consumeUnitText ? consumeUnitText.replace(/^\//, '').trim() : consumeUnit;
}

// formatCarrotAmount 将数量和算力萝卜单位组合，英文单位前补空格。
function formatCarrotAmount(amount: string, translate: TranslateText) {
  const carrotText = translate((v) => v['302']);
  const separator = /^[A-Za-z]/.test(carrotText) ? ' ' : '';
  return `${amount}${separator}${carrotText}`;
}

// getRowKey 使用模型名和序号生成稳定 key，兼容后端暂不下发 id 的表格数据。
function getRowKey(item: CoreModelPriceTableItem, index: number) {
  return `${item.modelType || 'model'}-${item.modelName || index}`;
}

// getMemberPriceFromList 从数组结构里按会员名或会员等级取对应价格。
function getMemberPriceFromList(item: CoreModelPriceTableItem, column: MemberColumn) {
  return item.levelPrices?.find((priceItem) => Number(priceItem.level) === column.level);
}

// formatMemberPriceCell 将后端价格对象转成设计稿里的“萝卜（人民币）/单位”文案。
function formatMemberPriceCell(
  priceInfo: CoreModelPriceTableMemberPrice | string | number | undefined,
  item: CoreModelPriceTableItem,
  translate: TranslateText,
) {
  if (priceInfo == null) return '';
  if (typeof priceInfo === 'string') return priceInfo;
  if (typeof priceInfo === 'number') return formatCarrotAmount(formatNumber(priceInfo), translate);
  if (priceInfo.displayText) return priceInfo.displayText;

  const consumeCount = formatNumber(priceInfo.consumeCount ?? item.consumeCount);
  const price = formatNumber(priceInfo.price ?? item.price);
  const consumeUnit = getConsumeUnitText(item.consumeUnit, item.consumeUnitLabel, translate);
  if (!consumeCount && !price) return '';
  const carrotAmount = formatCarrotAmount(consumeCount, translate);
  if (!price) return `${carrotAmount}/${consumeUnit}`;
  return `${carrotAmount}(¥${price})/${consumeUnit}`;
}

// getPriceTexts 按接口表头顺序展示 levelPrices 里的会员价格矩阵。
function getPriceTexts(item: CoreModelPriceTableItem, memberColumns: MemberColumn[], translate: TranslateText) {
  return memberColumns.map((column) => {
    const priceInfo = getMemberPriceFromList(item, column);
    return formatMemberPriceCell(priceInfo, item, translate);
  }).map((text) => text || '-');
}

// CoreModalPricing 渲染会员接口下发的核心模型积分消耗单价表。
function CoreModalPricing({ priceTable }: CoreModalPricingProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const translate = languageStore.smartcatTranslationText;
  const items = priceTable?.items || [];
  const memberColumns = getMemberColumns(priceTable);
  if (!items.length || !memberColumns.length) return null;

  return (
    <section className={styles.wrapper}>
      <div className={styles.title}>
        {priceTable?.title || translate((v) => v.core_model_points_consumption_rate_table)}
      </div>
      <Table containerClassName={styles.tableContainer} className={styles.priceTable}>
        <TableHeader>
          <TableRow>
            <TableHead className={styles.modelHead}>{priceTable?.headers?.[0]?.label || translate((v) => v.core_model)}</TableHead>
            {memberColumns.map((column) => (
              <TableHead key={column.key} className={styles.memberHead}>{column.title}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className={styles.priceTableBody}>
          {items.map((item, index) => (
            <TableRow key={getRowKey(item, index)} className={styles.priceTableRow}>
              <TableCell className={styles.modelCell}>
                <div className={styles.modelContent}>
                  {item.modelIconKey ? (
                    <IconFont
                      name={item.modelIconKey}
                      fontSize={16}
                      className={styles.modelIcon}
                    />
                  ) : null}
                  <span className={styles.modelText}>
                    <span className={styles.modelName}>{item.modelName || '-'}</span>
                    <span className={styles.modelType}>{getModelTypeText(item, translate)}</span>
                  </span>
                </div>
              </TableCell>
              {getPriceTexts(item, memberColumns, translate).map((text, textIndex) => (
                <TableCell key={`${getRowKey(item, index)}-${memberColumns[textIndex].key}`} className={styles.priceCell}>
                  {text}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className={styles.footerText}>
        {priceTable?.footer || translate(
          (v) => v._points_consumption_may_vary_based_on_adjustments_to_the_model_provider_s_policies_roboneo_reserves_the_right_of_final_interpretation_1_computing_radish_points_0_032_,
        )}
      </div>
    </section>
  );
}

export default CoreModalPricing;
